import React, { useCallback, useEffect, useMemo, useState } from "react";

import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import { CircularProgress, Stack, SxProps, TextField, Theme, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { RoleComponentGuard } from "components";
import { AccessDenied } from "components/Access-denied/AccessDenied";
import { SelectAutocomplete } from "components/forms";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useActionHook, useNavigate, useQueryHook } from "hooks";
import { useDateFormatSelect, usePickUserPermissions, useReportsActions, useReportsMetaSelector } from "store";
import { DealBase, UserPermissionType } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { RoleName } from "../../constants";

import "./ExternalReport.styles.scss";

export const ExternalReport: React.FC = () => {
  const dateFormat = useDateFormatSelect();
  const { getExternalMeasuresAction, getExternalReportsAction, getReportAction, getMetaAction } = useReportsActions();

  useEffect(() => {
    getMetaAction();
  }, []);

  const { data: measures } = useQueryHook(getExternalMeasuresAction);
  const { data: dynamicReports } = useQueryHook(getExternalReportsAction);

  const { deals } = useReportsMetaSelector();
  const externalReport = usePickUserPermissions("external_reports");
  const noAccessPermission = externalReport && externalReport === UserPermissionType.NO_ACCESS;
  const navigate = useNavigate();

  const initialValues: {
    accounting_date: string;
    deals: DealBase[];
    measure: string[];
  } = useMemo(
    () => ({
      accounting_date: dayjs(new Date()).toISOString(),
      deals: [],
      measure: []
    }),
    []
  );

  const { setFieldValue, values } = useFormik({
    initialValues,
    onSubmit: () => {},
    enableReinitialize: true
  });

  const { action: getReport } = useActionHook(getReportAction);
  const [loadingReportId, setLoadingReportId] = useState<number | null>(null);

  const onDownloadDynamicReport = useCallback(
    async (reportId: number) => {
      setLoadingReportId(reportId);
      try {
        await getReport({
          id: reportId,
          date: values.accounting_date,
          deal_ids: values.deals.map((d) => d.id),
          parameters: values.measure
        });
      } finally {
        setLoadingReportId(null);
      }
    },
    [values, getReport]
  );

  // Combine all reports for flexible layout
  const allReports = useMemo(() => {
    const dynamicReportsList =
      dynamicReports
        ?.sort((a, b) => a.order - b.order) // Sort by order field
        ?.map((report, index) => ({
          id: report.id,
          name: report.name,
          onClick: () => onDownloadDynamicReport(report.id),
          borderColor: `hsl(${index * 60}, 70%, 80%)`
        })) || [];

    return [...dynamicReportsList];
  }, [dynamicReports, onDownloadDynamicReport]);

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
      hideChild={noAccessPermission}
      RenderInstead={<AccessDenied redirectClick={() => navigate({ route: "main" })} title="Back home" />}
    >
      <div className="extended-report page">
        <div className="extended-report__parameters">
          <div className="extended-report__parameters-rest">
            <div className="row">
              <DesktopDatePicker
                format={dateFormat}
                views={["year", "month", "day"]}
                label="Date"
                slotProps={{
                  popper: {
                    sx: {
                      ".MuiPickersCalendarHeader-labelContainer": {
                        fontSize: "0.9rem"
                      }
                    },
                    placement: "bottom-end"
                  },

                  textField: {
                    fullWidth: true,
                    sx: {
                      ".MuiInputBase-root": {
                        height: "48px"
                      },
                      input: {
                        fontSize: "16px",
                        color: "#5d7782"
                      },
                      "& label": { fontSize: "16px", color: "#5d7782" },
                      ".MuiOutlinedInput-root": {
                        "& > fieldset": { borderColor: "#5d7782" }
                      },
                      "& .MuiIconButton-root": {
                        color: "#5d7782"
                      }
                    }
                  },
                  inputAdornment: { position: "start", color: "#5d7782" }
                }}
                value={dayjs(values.accounting_date)}
                onChange={async (value) => {
                  if (value && value.isValid()) {
                    setFieldValue("accounting_date", toIsoMidday(value.toISOString()));
                  }
                }}
              />
            </div>
            <div className="row">
              <SelectAutocomplete<DealBase, true>
                options={deals}
                value={values.deals}
                multiple
                selectAll
                setValue={(newValue) => setFieldValue("deals", newValue)}
                renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: DealBase) => (
                  <li {...props} key={option.id}>
                    {option.title}
                  </li>
                )}
                getOptionLabel={(option) => option.title}
                renderInput={(params) => <TextField {...params} label="Deal" size="medium" />}
                compareParam={(p) => p.title}
                disableCloseOnSelect
                limitTags={1}
                sx={comminSx}
              />
            </div>
            <div className="row">
              <SelectAutocomplete<string, true>
                options={measures ? measures : []}
                value={values.measure}
                multiple
                selectAll
                setValue={(newValue) => setFieldValue("measure", newValue)}
                renderInput={(params) => <TextField {...params} label="Parameters" size="medium" />}
                disableCloseOnSelect
                limitTags={1}
                sx={comminSx}
              />
            </div>
          </div>
        </div>
        <div className="extended-report__parameters">
          <div className="extended-report__parameters-title">Choose Report to Download</div>
          <div className="extended-report__parameters-separator"></div>
          <div className="extended-report__parameters-cards-box">
            {allReports.map((report) => {
              const isLoading = loadingReportId === report.id;
              return (
                <div
                  key={report.id}
                  style={{
                    borderColor: report.borderColor,
                    opacity: isLoading ? 0.6 : 1,
                    pointerEvents: loadingReportId !== null ? "none" : "auto",
                    cursor: loadingReportId !== null ? "not-allowed" : "pointer"
                  }}
                  onClick={loadingReportId !== null ? undefined : report.onClick}
                  className="extended-report__parameters-card"
                >
                    {isLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Stack direction="row" alignItems="center" gap="8px" sx={{ overflow: "hidden" }}>
                        <AutoGraphIcon sx={{ flexShrink: 0 }} />
                        <MaterialTooltip title={report.name} isShow>
                          <Typography
                            noWrap
                            sx={{
                              fontSize: "16px",
                              fontWeight: 400,
                              color: "#071F35",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}
                          >
                            {report.name}
                          </Typography>
                        </MaterialTooltip>
                      </Stack>
                    )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </RoleComponentGuard>
  );
};

const comminSx: SxProps<Theme> = {
  ".MuiChip-label": {
    maxWidth: "215px",
    fontSize: "15px"
  },
  ".MuiInputLabel-root": {
    fontSize: "16px",
    top: "-3px"
  },
  ".MuiOutlinedInput-root": {
    "& > fieldset": { borderColor: "#5d7782" },
    padding: "5px"
  },
  ".MuiInputBase-root": {
    paddingRight: "40px !important"
  }
};
